import React from "react";
import { UserRound, UserRoundX, GitBranch, CircleDot, Star, Tag, Tags, CalendarClock, Archive, RotateCcw, Trash2, ChevronRight, Zap, } from "lucide-react";

export default function BulkActionsMenu({
    canDelete = false,
    activeAction,
    selectedCount,
    onAction,
}) {
    const items = [
        { label: "Assign User", value: "ASSIGN_USER", icon: UserRound, submenu: true, },
        { label: "Unassign User", value: "UNASSIGN_USER", icon: UserRoundX, },

        // PIPELINE
        { label: "Change Stage", value: "CHANGE_STAGE", icon: GitBranch, submenu: true, dividerBefore: true, },
        { label: "Change Status", value: "CHANGE_STATUS", icon: CircleDot, submenu: true, },
        { label: "Change Priority", value: "CHANGE_PRIORITY", icon: Star, submenu: true, },

        // ORGANIZATION
        { label: "Add Tag", value: "ADD_TAG", icon: Tag, submenu: true, dividerBefore: true, },
        { label: "Replace Tag", value: "REPLACE_TAG", icon: Tags, submenu: true, },
        { label: "Activity", value: "ACTIVITY", icon: CalendarClock, submenu: true, },
    ];

    const systemActions = [
        { label: "Archive", value: "ARCHIVE", icon: Archive, },
        { label: "Restore", value: "RESTORE", icon: RotateCcw, },
        { label: "Delete", value: "DELETE", icon: Trash2, danger: true, },
    ];

    return (
        <div className="w-[272px] overflow-hidden rounded-2xl border border-[#d6e5eb] bg-white text-[#405c6d] shadow-[0_16px_40px_rgba(15,43,66,0.14)]">
            {/* Header */}
            <div className="border-b border-[#e5eef2] px-3.5 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#edf8fb] text-[#2f7faf]">
                            <Zap size={14} strokeWidth={1.8} />
                        </div>

                        <div>
                            <div className="text-sm font-semibold tracking-[-0.01em] text-[#173044]">
                                Bulk actions
                            </div>

                            <div className="mt-0.5 text-xs text-[#8197a4]">
                                Update selected leads
                            </div>
                        </div>
                    </div>

                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-[#edf5f8] px-1.5 text-xs font-semibold text-[#647d8e]">
                        {selectedCount}
                    </span>
                </div>
            </div>

            {/* Actions */}
            <div className="max-h-[70vh] overflow-y-auto p-1.5">
                <div className="space-y-0.5">
                    {items.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeAction === item.value;

                        return (
                            <React.Fragment key={item.value}>
                                {item.dividerBefore && (
                                    <div className="my-2 h-px w-full bg-[#e5eef2]" />
                                )}

                                <button
                                    type="button"
                                    onClick={() => onAction(item.value)}
                                    className={`group relative flex min-h-10 w-full items-center rounded-xl px-2 text-left transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#50b8e7]/30 ${isActive ? "bg-[#edf8fb] text-[#236fa3]" : "text-[#526e7e] hover:bg-[#f5fafc] hover:text-[#173044]"}`}
                                >
                                    <span className={`absolute bottom-2 left-0 top-2 w-0.5 rounded-full ${isActive ? "bg-[#2f84bd] opacity-100" : "opacity-0"}`}/>

                                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? "bg-white text-[#2f84bd] shadow-sm" : "text-[#8197a4] group-hover:bg-white group-hover:text-[#526e7e] group-hover:shadow-sm"}`}>
                                        <Icon size={15} strokeWidth={1.8} />
                                    </span>

                                    <span className={`min-w-0 flex-1 text-[13px] ${isActive ? "font-semibold text-[#236fa3]" : "font-medium text-[#526e7e]"}`}>
                                        {item.label}
                                    </span>

                                    {item.submenu && (
                                        <ChevronRight
                                            size={14}
                                            strokeWidth={1.8}
                                            className={`shrink-0 ${isActive ? "translate-x-0.5 text-[#2f84bd]" : "text-[#afc0c9] group-hover:translate-x-0.5 group-hover:text-[#647d8e]"}`}
                                        />
                                    )}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Record actions */}
                <div className="mt-2 border-t border-[#e5eef2] pt-2">
                    <div className="mb-1 px-2 py-1 text-xs font-semibold text-[#8197a4]">
                        Lead controls
                    </div>

                    <div className="space-y-0.5">
                        {systemActions.filter((item) => item.value !== "DELETE" || canDelete).map((item) => {
                            const Icon = item.icon;

                            return (
                                <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => onAction(item.value)}
                                    className={`group flex min-h-10 w-full items-center rounded-xl px-2 text-left transition-colors duration-100 ${item.danger ? "text-red-600 hover:bg-red-50" : "text-[#526e7e] hover:bg-[#f5fafc] hover:text-[#173044]"}`}
                                >
                                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${item.danger ? "text-red-500 group-hover:bg-red-100" : "text-[#8197a4] group-hover:bg-white group-hover:text-[#526e7e] group-hover:shadow-sm"}`}>
                                        <Icon size={15} strokeWidth={1.8}/>
                                    </span>

                                    <span className={`flex-1 text-[13px] font-medium ${item.danger ? "text-red-600" : "text-[#526e7e] group-hover:text-[#173044]"}`}>
                                        {item.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
